/**
 * TaskCreatorModal — wizard в 3 шага:
 *   1. Что? (название + описание)
 *   2. Кому? (исполнители)
 *   3. Когда + Куда? (дедлайн + задачник)
 * После создания родитель отправляет системное сообщение в чат с @mentions.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { createTask, type Task } from "@/api/tasks";
import { fetchActiveUsers, userAvatarUrl, type ChatTopic, type UserShort } from "@/api/dashboard";

interface Props {
  topics: ChatTopic[];
  sourceTopic: ChatTopic | null;
  onClose: () => void;
  onCreated: (task: Task, assignees: UserShort[]) => void;
  inline?: boolean;
  /** Опциональный prefill дедлайна (например, при двойном клике по ячейке Day-view). */
  initialDeadline?: Date | null;
}

function fromDatetimeLocalValue(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
void fromDatetimeLocalValue;

const WEEKDAYS_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"] as const;
const WEEKDAYS_FULL = ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"] as const;
void WEEKDAYS_FULL;

const DEPT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Снабжение",
};

/** Возвращает 7 быстрых дат: Сегодня, Завтра, затем рабочие дни Пн-Пт.
 *  Если завтра уже Пн → пропускаем дубль. Игнорируем Сб/Вс. */
function buildDateChips(): Array<{ date: Date; label: string; sub?: string }> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const chips: Array<{ date: Date; label: string; sub?: string }> = [
    { date: new Date(today), label: "Сегодня" },
    { date: new Date(tomorrow), label: "Завтра" },
  ];
  // Добавляем следующие рабочие дни (Пн-Пт), пропуская Сб/Вс и сегодня/завтра
  const cursor = new Date(tomorrow);
  while (chips.length < 7) {
    cursor.setDate(cursor.getDate() + 1);
    const dow = cursor.getDay(); // 0=Вс, 6=Сб
    if (dow === 0 || dow === 6) continue;  // skip weekends
    chips.push({
      date: new Date(cursor),
      label: WEEKDAYS_SHORT[dow],
      sub: cursor.toLocaleDateString("ru-RU", { day: "numeric", month: "short" }),
    });
  }
  return chips;
}

/** Полный список времён 00:00, 00:30, ... 23:30 с шагом 30 минут. */
function buildTimeSlots(): Array<{ h: number; m: number; label: string }> {
  const out: Array<{ h: number; m: number; label: string }> = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push({ h, m, label: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
    }
  }
  return out;
}
const TIME_SLOTS = buildTimeSlots();

function toIsoWithTime(date: Date, h: number, m: number): string {
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function fmtDateChip(d: Date): string {
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function fmtFullDeadline(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", { day: "2-digit", month: "short", weekday: "short", hour: "2-digit", minute: "2-digit" });
}
void fmtFullDeadline;

const STEPS = [
  { id: 1, label: "Что?" },
  { id: 2, label: "Кому?" },
  { id: 3, label: "Когда?" },
  { id: 4, label: "Итого" },
] as const;

const MOBILE_STEPS = [
  { id: 1, label: "Название" },
  { id: 2, label: "Описание" },
  { id: 3, label: "Исполнитель" },
  { id: 4, label: "Дедлайн" },
  { id: 5, label: "Итого" },
] as const;

export function TaskCreatorModal({ topics, sourceTopic, onClose, onCreated, inline = false, initialDeadline = null }: Props) {
  const tasksTopics = useMemo(() => topics.filter(t => t.kind === "tasks"), [topics]);
  const defaultTopicId = useMemo(() => {
    if (sourceTopic?.department) {
      const matching = tasksTopics.find(t => t.department === sourceTopic.department);
      if (matching) return matching.id;
    }
    return tasksTopics[0]?.id ?? null;
  }, [tasksTopics, sourceTopic]);

  const [step, setStep] = useState<number>(1);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 700px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(max-width: 700px)");
    const cb = () => setIsMobile(m.matches);
    m.addEventListener("change", cb);
    return () => m.removeEventListener("change", cb);
  }, []);
  const activeStepsArr = isMobile ? MOBILE_STEPS : STEPS;
  const totalSteps = activeStepsArr.length;
  const [targetTopicId, setTargetTopicId] = useState<number | null>(defaultTopicId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Шаг 3 (Когда?): дата + время как chips. Если оба null → дедлайна нет.
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (!initialDeadline) return null;
    const d = new Date(initialDeadline);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedTime, setSelectedTime] = useState<{ h: number; m: number } | null>(() => {
    if (!initialDeadline) return null;
    return { h: initialDeadline.getHours(), m: initialDeadline.getMinutes() };
  });
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customTimeOpen, setCustomTimeOpen] = useState(false);
  const [assignees, setAssignees] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState("");
  // Шаг 2 (Кому?): дефолтный фильтр = отдел исходного чата. Юзер может
  // переключить на другой отдел или ввести поиск — тогда показывается
  // совпадение во ВСЕХ отделах (поиск имеет приоритет над фильтром).
  const [deptFilter, setDeptFilter] = useState<string | null>(sourceTopic?.department ?? null);

  useEffect(() => { if (targetTopicId == null && defaultTopicId != null) setTargetTopicId(defaultTopicId); }, [defaultTopicId, targetTopicId]);

  const usersQ = useQuery({
    queryKey: ["users/active"],
    queryFn: fetchActiveUsers,
    staleTime: 5 * 60_000,
  });
  const users = usersQ.data ?? [];

  // На мобиле — авто-подбор Календаря по отделу первого выбранного исполнителя
  useEffect(() => {
    if (!isMobile) return;
    if (assignees.size === 0) return;
    const firstId = Array.from(assignees)[0];
    const u = users.find(x => x.id === firstId);
    const dept = u?.departments?.[0];
    if (!dept) return;
    const matching = tasksTopics.find(t => t.department === dept);
    if (matching && matching.id !== targetTopicId) setTargetTopicId(matching.id);
  }, [isMobile, assignees, users, tasksTopics, targetTopicId]);

  // Список отделов: distinct из users + всегда добавляем sourceTopic.department
  const availableDepts = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) for (const d of (u.departments ?? [])) if (d) set.add(d);
    if (sourceTopic?.department) set.add(sourceTopic.department);
    return Array.from(set);
  }, [users, sourceTopic]);

  // Отфильтрованные юзеры для шага 2
  const visibleUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (q) {
      // search active → показываем ВСЕХ юзеров что матчат (игнорируем deptFilter)
      return users.filter(u =>
        (u.display_name ?? "").toLowerCase().includes(q) ||
        (u.position ?? "").toLowerCase().includes(q),
      );
    }
    if (deptFilter == null) return users;
    return users.filter(u => (u.departments ?? []).includes(deptFilter));
  }, [users, userSearch, deptFilter]);

  function toggleAssignee(uid: string) {
    setAssignees(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!targetTopicId) throw new Error("Выберите задачник");
      const t = await createTask(targetTopicId, {
        title: title.trim(),
        description: description.trim() || undefined,
        deadline_at: selectedDate
          ? toIsoWithTime(selectedDate, selectedTime?.h ?? 23, selectedTime?.m ?? 59)
          : null,
        assignee_user_ids: Array.from(assignees),
      });
      const pickedUsers = users.filter(u => assignees.has(u.id));
      onCreated(t, pickedUsers);
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canNext = isMobile
    ? (step === 1 ? title.trim().length > 0 : true)
    : (step === 1 ? title.trim().length > 0
       : step === 2 ? true
       : step === 3 ? !!targetTopicId
       : true);
  const isLastStep = step >= totalSteps;
  const canSave = !!title.trim() && !!targetTopicId;

  // На мобиле ВСЕГДА (включая slash-inline) — bottom-sheet через position:fixed.
  // Перебиваем любые CSS — inline-стили с !important выигрывают.
  const mobileSheetStyle: React.CSSProperties | undefined = isMobile
    ? {
        width: "100vw",
        maxWidth: "100vw",
        height: "auto",
        maxHeight: "85vh",
        borderRadius: "16px 16px 0 0",
        margin: 0,
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: "auto",
        zIndex: 10000,
        boxShadow: "0 -8px 32px rgba(0,0,0,0.18)",
      }
    : undefined;
  const inner = (
    <div
      className={`task-modal task-modal-wizard${inline ? " task-modal-inline" : ""}`}
      style={mobileSheetStyle}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="task-wiz-header">
        <div className="task-wiz-title-row">
          <div className="task-wiz-title">Новая задача</div>
          <button
            type="button"
            className="task-wiz-close"
            onClick={onClose}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div className="task-wiz-steps">
          {(isMobile ? activeStepsArr.filter(s => s.id === step) : activeStepsArr).map((s) => (
            <div
              key={s.id}
              className={`task-wiz-step${step === s.id ? " is-active" : ""}${s.id < step ? " is-done" : ""}`}
            >
              <span className="task-wiz-step-num">{s.id < step ? "✓" : s.id}</span>
              <span className="task-wiz-step-label">
                {s.label}
                {isMobile && <span className="task-wiz-step-of"> · шаг {step} из {totalSteps}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="task-wiz-body">
        {/* ─── МОБИЛЬНЫЕ 5 ШАГОВ ─── */}
        {isMobile && step === 1 && (
          <label className="task-field">
            <span className="task-field-label">Название *</span>
            <input
              type="text"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Что нужно сделать"
              onKeyDown={(e) => {
                if (e.key === "Enter" && canNext) { e.preventDefault(); setStep(s => s + 1); }
              }}
            />
          </label>
        )}
        {isMobile && step === 2 && (
          <label className="task-field">
            <span className="task-field-label">Описание (необязательно)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Детали"
              rows={6}
              autoFocus
            />
          </label>
        )}
        {/* МОБ ШАГ 3 — ИСПОЛНИТЕЛЬ */}
        {isMobile && step === 3 && (
          <div className="task-field">
            <span className="task-field-label">Кому ставим? ({assignees.size})</span>
            <input
              type="search"
              placeholder="Поиск по имени…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="task-user-search"
              style={{ fontSize: 16 }}
            />
            <div className="task-user-grid">
              {visibleUsers.map(u => {
                const checked = assignees.has(u.id);
                const avaUrl = userAvatarUrl(u.id, u.avatar_filename ?? null);
                const initial = (u.display_name ?? "?")[0];
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`task-user-tile${checked ? " is-selected" : ""}`}
                    onClick={() => toggleAssignee(u.id)}
                    title={u.display_name ?? ""}
                  >
                    <div className="task-user-tile-avatar-wrap">
                      {avaUrl ? (
                        <img src={avaUrl} alt="" className="task-user-tile-avatar" loading="lazy"/>
                      ) : (
                        <span className="task-user-tile-avatar task-user-tile-avatar-fallback">
                          {initial}
                        </span>
                      )}
                      {checked && (
                        <span className="task-user-tile-check">✓</span>
                      )}
                    </div>
                    <div className="task-user-tile-name">{u.display_name ?? "(без имени)"}</div>
                  </button>
                );
              })}
              {visibleUsers.length === 0 && <div className="task-user-empty">Никто не найден</div>}
            </div>
          </div>
        )}
        {/* МОБ ШАГ 4 — ДЕДЛАЙН (дата + время вместе) */}
        {isMobile && step === 4 && (() => {
          const dateChips = buildDateChips();
          const selectedDateKey = selectedDate ? selectedDate.toDateString() : null;
          const isCustomDateSelected = !!(selectedDate && !dateChips.some(c => c.date.toDateString() === selectedDateKey));
          const customDateValue = (customDateOpen || isCustomDateSelected)
            ? (selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}` : "")
            : "";
          const nowMs = Date.now();
          return (
            <>
              <div className="task-field">
                <span className="task-field-label">Дата</span>
                <div className="task-chip-row">
                  {dateChips.map((c, i) => {
                    const isActive = selectedDateKey === c.date.toDateString();
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`task-chip${isActive ? " is-active" : ""}`}
                        onClick={() => { setSelectedDate(new Date(c.date)); setCustomDateOpen(false); }}
                      >
                        <span className="task-chip-label">{c.label}</span>
                        {c.sub && <span className="task-chip-sub">{c.sub}</span>}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={`task-chip${isCustomDateSelected || customDateOpen ? " is-active" : ""}`}
                    onClick={() => setCustomDateOpen(true)}
                  >
                    <span className="task-chip-label">Другая…</span>
                  </button>
                </div>
                {(customDateOpen || isCustomDateSelected) && (
                  <input
                    type="date"
                    value={customDateValue}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m, d] = e.target.value.split("-").map(Number);
                        const dt = new Date(y, m - 1, d); dt.setHours(0, 0, 0, 0);
                        setSelectedDate(dt);
                      } else setSelectedDate(null);
                    }}
                    style={{ width: "100%", padding: "10px 12px", marginTop: 8, border: "1px solid var(--tg-line, #e4e4e4)", borderRadius: 8, fontSize: 16, background: "#fff" }}
                  />
                )}
              </div>
              {selectedDate && (
                <div className="task-field">
                  <span className="task-field-label">Время</span>
                  <div className="task-chip-row task-chip-row-times">
                    {TIME_SLOTS.map((t) => {
                      const isActive = selectedTime?.h === t.h && selectedTime?.m === t.m;
                      const slot = new Date(selectedDate); slot.setHours(t.h, t.m, 0, 0);
                      const isPast = slot.getTime() <= nowMs;
                      return (
                        <button
                          key={t.label}
                          type="button"
                          disabled={isPast}
                          className={`task-chip task-chip-time${isActive ? " is-active" : ""}${isPast ? " is-past" : ""}`}
                          onClick={() => { if (!isPast) { setSelectedTime({ h: t.h, m: t.m }); setCustomTimeOpen(false); } }}
                        >
                          <span className="task-chip-label">{t.label}</span>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={`task-chip${customTimeOpen ? " is-active" : ""}`}
                      onClick={() => setCustomTimeOpen(true)}
                    >
                      <span className="task-chip-label">Другое…</span>
                    </button>
                  </div>
                  {customTimeOpen && (
                    <input
                      type="time"
                      value={selectedTime ? `${String(selectedTime.h).padStart(2, "0")}:${String(selectedTime.m).padStart(2, "0")}` : ""}
                      onChange={(e) => {
                        if (e.target.value) { const [h, m] = e.target.value.split(":").map(Number); setSelectedTime({ h, m }); }
                        else setSelectedTime(null);
                      }}
                      style={{ width: "100%", padding: "10px 12px", marginTop: 8, border: "1px solid var(--tg-line, #e4e4e4)", borderRadius: 8, fontSize: 16, background: "#fff" }}
                    />
                  )}
                </div>
              )}
              {!selectedDate && (
                <div className="task-wiz-hint">Можно пропустить — задача без срока</div>
              )}
            </>
          );
        })()}
        {/* МОБ ШАГ 5 — ИТОГО */}
        {isMobile && step === 5 && (() => {
          const pickedAssignees = users.filter(u => assignees.has(u.id));
          const targetName = tasksTopics.find(t => t.id === targetTopicId)?.name ?? "—";
          return (
            <div className="task-wiz-summary-full">
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Название</div>
                <div className="task-wiz-summary-block-value">{title.trim() || "—"}</div>
              </div>
              {description.trim() && (
                <div className="task-wiz-summary-block">
                  <div className="task-wiz-summary-block-label">Описание</div>
                  <div className="task-wiz-summary-block-value" style={{ whiteSpace: "pre-wrap", fontWeight: 400 }}>
                    {description.trim()}
                  </div>
                </div>
              )}
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">
                  Исполнители ({pickedAssignees.length})
                </div>
                <div className="task-wiz-summary-assignees">
                  {pickedAssignees.length === 0 && (
                    <span style={{ color: "var(--tg-text-secondary, #9aa)", fontSize: 13 }}>
                      Никто не назначен
                    </span>
                  )}
                  {pickedAssignees.map(u => {
                    const avaUrl = userAvatarUrl(u.id, u.avatar_filename ?? null);
                    return (
                      <span key={u.id} className="task-wiz-summary-assignee">
                        {avaUrl ? (
                          <img src={avaUrl} alt="" className="task-wiz-summary-ava" loading="lazy"/>
                        ) : (
                          <span className="task-wiz-summary-ava task-wiz-summary-ava-fb">
                            {(u.display_name ?? "?")[0]}
                          </span>
                        )}
                        <span>{u.display_name ?? "—"}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Дедлайн</div>
                <div className="task-wiz-summary-block-value">
                  {selectedDate
                    ? `${fmtDateChip(selectedDate)}${selectedTime ? `, до ${String(selectedTime.h).padStart(2, "0")}:${String(selectedTime.m).padStart(2, "0")}` : ", до конца дня"}`
                    : "Без срока"}
                </div>
              </div>
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Календарь</div>
                <div className="task-wiz-summary-block-value">{targetName}</div>
              </div>
            </div>
          );
        })()}

        {/* ─── ДЕСКТОП 4 ШАГА (без изменений) ─── */}
        {!isMobile && step === 1 && (
          <>
            <label className="task-field">
              <span className="task-field-label">Название *</span>
              <input
                type="text"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Что нужно сделать"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canNext) {
                    e.preventDefault();
                    setStep(2);
                  }
                }}
              />
            </label>
            <label className="task-field">
              <span className="task-field-label">Описание</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Детали (необязательно)"
                rows={3}
              />
            </label>
          </>
        )}

        {!isMobile && step === 2 && (
          <div className="task-field">
            <span className="task-field-label">Исполнители ({assignees.size})</span>
            {/* Dept tabs: дефолт = отдел чата, можно переключить или искать */}
            <div className="task-dept-tabs">
              {availableDepts.map(d => (
                <button
                  key={d}
                  type="button"
                  className={`task-dept-tab${deptFilter === d && !userSearch.trim() ? " is-active" : ""}`}
                  onClick={() => { setDeptFilter(d); setUserSearch(""); }}
                >
                  {DEPT_LABELS[d] ?? d}
                </button>
              ))}
              <button
                type="button"
                className={`task-dept-tab${deptFilter == null && !userSearch.trim() ? " is-active" : ""}`}
                onClick={() => { setDeptFilter(null); setUserSearch(""); }}
              >
                Все
              </button>
            </div>
            <input
              type="search"
              placeholder="Не нашли по аватарке? Найти по имени…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="task-user-search"
            />
            {/* Grid аватарок */}
            <div className="task-user-grid">
              {visibleUsers.map(u => {
                const checked = assignees.has(u.id);
                const avaUrl = userAvatarUrl(u.id, u.avatar_filename ?? null);
                const initial = (u.display_name ?? "?")[0];
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`task-user-tile${checked ? " is-selected" : ""}`}
                    onClick={() => toggleAssignee(u.id)}
                    title={u.display_name ?? ""}
                  >
                    <div className="task-user-tile-avatar-wrap">
                      {avaUrl ? (
                        <img src={avaUrl} alt="" className="task-user-tile-avatar" loading="lazy"/>
                      ) : (
                        <span className="task-user-tile-avatar task-user-tile-avatar-fallback">
                          {initial}
                        </span>
                      )}
                      {checked && (
                        <span className="task-user-tile-check">✓</span>
                      )}
                    </div>
                    <div className="task-user-tile-name">{u.display_name ?? "(без имени)"}</div>
                  </button>
                );
              })}
              {visibleUsers.length === 0 && <div className="task-user-empty">Никто не найден</div>}
            </div>
          </div>
        )}

        {!isMobile && step === 3 && (() => {
          const dateChips = buildDateChips();
          const selectedDateKey = selectedDate ? selectedDate.toDateString() : null;
          const customDateValue = (customDateOpen || (selectedDate && !dateChips.some(c => c.date.toDateString() === selectedDateKey)))
            ? (selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}` : "")
            : "";
          const isCustomDateSelected = !!(selectedDate && !dateChips.some(c => c.date.toDateString() === selectedDateKey));
          return (
            <>
              <div className="task-field">
                <span className="task-field-label">Когда нужно сделать?</span>
                <div className="task-chip-row">
                  {dateChips.map((c, i) => {
                    const isActive = selectedDateKey === c.date.toDateString();
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`task-chip${isActive ? " is-active" : ""}`}
                        onClick={() => { setSelectedDate(new Date(c.date)); setCustomDateOpen(false); }}
                      >
                        <span className="task-chip-label">{c.label}</span>
                        {c.sub && <span className="task-chip-sub">{c.sub}</span>}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={`task-chip${isCustomDateSelected || customDateOpen ? " is-active" : ""}`}
                    onClick={() => setCustomDateOpen(true)}
                  >
                    <span className="task-chip-label">Другая…</span>
                  </button>
                </div>
                {(customDateOpen || isCustomDateSelected) && (
                  <input
                    type="date"
                    autoFocus
                    value={customDateValue}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m, d] = e.target.value.split("-").map(Number);
                        const dt = new Date(y, m - 1, d);
                        dt.setHours(0, 0, 0, 0);
                        setSelectedDate(dt);
                      } else {
                        setSelectedDate(null);
                      }
                    }}
                    style={{
                      width: "100%", padding: "8px 12px", marginTop: 6,
                      border: "1px solid var(--tg-line, #e4e4e4)", borderRadius: 8,
                      fontSize: 14, background: "#fff",
                    }}
                  />
                )}
                {selectedDate && (
                  <>
                    <span className="task-field-label" style={{ marginTop: 12 }}>До скольки?</span>
                    <div className="task-chip-row task-chip-row-times">
                      {(() => {
                        const nowMs = Date.now();
                        return TIME_SLOTS.map((t) => {
                          const isActive = selectedTime?.h === t.h && selectedTime?.m === t.m;
                          const slot = new Date(selectedDate);
                          slot.setHours(t.h, t.m, 0, 0);
                          const isPast = slot.getTime() <= nowMs;
                          return (
                            <button
                              key={t.label}
                              type="button"
                              disabled={isPast}
                              className={`task-chip task-chip-time${isActive ? " is-active" : ""}${isPast ? " is-past" : ""}`}
                              onClick={() => { if (!isPast) { setSelectedTime({ h: t.h, m: t.m }); setCustomTimeOpen(false); } }}
                            >
                              <span className="task-chip-label">{t.label}</span>
                            </button>
                          );
                        });
                      })()}
                      <button
                        type="button"
                        className={`task-chip${customTimeOpen ? " is-active" : ""}`}
                        onClick={() => setCustomTimeOpen(true)}
                        title="Указать точное время"
                      >
                        <span className="task-chip-label">Другое…</span>
                      </button>
                    </div>
                    {customTimeOpen && (
                      <input
                        type="time"
                        autoFocus
                        value={selectedTime ? `${String(selectedTime.h).padStart(2, "0")}:${String(selectedTime.m).padStart(2, "0")}` : ""}
                        onChange={(e) => {
                          if (e.target.value) {
                            const [h, m] = e.target.value.split(":").map(Number);
                            setSelectedTime({ h, m });
                          } else {
                            setSelectedTime(null);
                          }
                        }}
                        style={{
                          width: "100%", padding: "8px 12px", marginTop: 6,
                          border: "1px solid var(--tg-line, #e4e4e4)", borderRadius: 8,
                          fontSize: 14, background: "#fff",
                        }}
                      />
                    )}
                  </>
                )}
                {!selectedDate && (
                  <div className="task-wiz-hint">Можно оставить пустым — задача без срока</div>
                )}
              </div>
              <label className="task-field">
                <span className="task-field-label">Календарь *</span>
                <select
                  value={targetTopicId ?? ""}
                  onChange={(e) => setTargetTopicId(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: "100%", padding: "8px 12px",
                    border: "1px solid var(--tg-line, #e4e4e4)", borderRadius: 8,
                    fontSize: 14, background: "#fff", fontFamily: "inherit",
                  }}
                >
                  {tasksTopics.length === 0 && <option value="">Нет доступных задачников</option>}
                  {tasksTopics.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
            </>
          );
        })()}

        {!isMobile && step === 4 && (() => {
          const pickedAssignees = users.filter(u => assignees.has(u.id));
          const targetName = tasksTopics.find(t => t.id === targetTopicId)?.name ?? "—";
          return (
            <div className="task-wiz-summary-full">
              <div className="task-wiz-summary-title">Проверьте задачу</div>
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Название</div>
                <div className="task-wiz-summary-block-value">{title.trim() || "—"}</div>
              </div>
              {description.trim() && (
                <div className="task-wiz-summary-block">
                  <div className="task-wiz-summary-block-label">Описание</div>
                  <div className="task-wiz-summary-block-value" style={{ whiteSpace: "pre-wrap", fontWeight: 400 }}>
                    {description.trim()}
                  </div>
                </div>
              )}
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">
                  Исполнители ({pickedAssignees.length})
                </div>
                <div className="task-wiz-summary-assignees">
                  {pickedAssignees.length === 0 && (
                    <span style={{ color: "var(--tg-text-secondary, #9aa)", fontSize: 13 }}>
                      Никто не назначен
                    </span>
                  )}
                  {pickedAssignees.map(u => {
                    const avaUrl = userAvatarUrl(u.id, u.avatar_filename ?? null);
                    return (
                      <span key={u.id} className="task-wiz-summary-assignee" title={u.display_name ?? ""}>
                        {avaUrl ? (
                          <img src={avaUrl} alt="" className="task-wiz-summary-ava" loading="lazy"/>
                        ) : (
                          <span className="task-wiz-summary-ava task-wiz-summary-ava-fb">
                            {(u.display_name ?? "?")[0]}
                          </span>
                        )}
                        <span>{u.display_name ?? "—"}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Дедлайн</div>
                <div className="task-wiz-summary-block-value">
                  {selectedDate
                    ? `${fmtDateChip(selectedDate)}${selectedTime ? `, до ${String(selectedTime.h).padStart(2, "0")}:${String(selectedTime.m).padStart(2, "0")}` : ", до конца дня"}`
                    : "Без срока"}
                </div>
              </div>
              <div className="task-wiz-summary-block">
                <div className="task-wiz-summary-block-label">Куда</div>
                <div className="task-wiz-summary-block-value">{targetName}</div>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="task-modal-actions task-wiz-actions">
        {step === 1 ? (
          <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
            <Icon name="x" size={14}/> Отмена
          </button>
        ) : (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setStep((s) => Math.max(1, s - 1))}>
            <Icon name="chevron_l" size={14}/> Назад
          </button>
        )}
        {!isLastStep ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canNext}
            onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
          >
            Дальше <Icon name="chevron_r" size={14}/>
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-sm"
            disabled={!canSave || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            Создать задачу
          </button>
        )}
      </div>
    </div>
  );
  // На мобиле оборачиваем даже inline-mode в полноэкранный backdrop
  // (slash-popup на телефоне = bottom-sheet, как обычная модалка).
  if (inline && !isMobile) return inner;
  const bgStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        padding: 0,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "stretch",
      }
    : { padding: 16 };
  return (
    <div className="task-modal-bg" style={bgStyle} onClick={onClose}>
      {inner}
    </div>
  );
}
